import React, { useEffect, useRef, useState } from "react";
import { Input, Button, Checkbox } from "antd";
import config from "../../config";

import "../../components/Subscription/checkout.css";

/**
 * The card form. Helcim.js reads these inputs out of the DOM **by id**, so every
 * id below is load-bearing — `helcimProcess()` looks for them literally. That is
 * also why the fields we already know are hidden inputs rather than omitted:
 * the values must be in the form, they just should not be asked for twice.
 *
 * What changed, and why:
 *   - Name on card, billing address and postal code were asked again here,
 *     one screen after the customer typed them. They are now inherited, behind
 *     the "same as billing address" checkbox every checkout uses — unticking it
 *     reveals the fields for a card billed somewhere else.
 *   - Expiry was two inputs labelled "Enter Month" / "Enter Year" with the
 *     label attached to neither; it is now MM / YY, side by side.
 *   - The CVV input carried `id="Enter CVV"`. Helcim.js looks for `cardCVV`, so
 *     it could never have read the security code off this form.
 *
 * @param {Object} helcimConfig  {"helcim js token", customerCode}
 * @param {Object} [billing]     what the customer just entered: first_name,
 *   last_name, address1, postalCode. Absent for a returning customer, in which
 *   case the cardholder fields are simply shown.
 * @param {string} [submitText]
 * @param {React.ReactNode} [chargeNote]  what will be charged, in plain words.
 */
const PaymentForm = ({ helcimConfig = {}, billing, submitText = "Start subscription", chargeNote }) => {
  const billingName = [billing && billing.first_name, billing && billing.last_name]
    .filter(Boolean)
    .join(" ")
    .trim();
  const haveBilling = !!(billingName && billing && billing.address1);

  const [cardNumber, setCardNumber] = useState("");
  const [cardExpiryMonth, setCardExpiryMonth] = useState("");
  const [cardExpiryYear, setCardExpiryYear] = useState("");
  const [cardCVV, setCardCVV] = useState("");
  const [sameAsBilling, setSameAsBilling] = useState(haveBilling);
  const [cardHolderName, setCardHolderName] = useState(billingName);
  const [cardHolderAddress, setCardHolderAddress] = useState((billing && billing.address1) || "");
  const [cardHolderPostalCode, setCardHolderPostalCode] = useState((billing && billing.postalCode) || "");
  const [loading, setLoading] = useState(false);
  const [grecaptcha, setGrecaptcha] = useState("");
  const resultRef = useRef(null);

  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://www.google.com/recaptcha/api.js?render=" + config.googleCaptch.code;
    script.async = true;
    document.body.appendChild(script);

    script.onload = () => {
      window.grecaptcha.ready(() => {
        window.grecaptcha
          .execute(config.googleCaptch.code, { action: "helcimJSCheckout" })
          .then((token) => setGrecaptcha(token));
      });
    };

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  const handleProcessClick = (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      window.helcimProcess();
    } catch (error) {
      console.error("Helcim process error:", error);
    }
    // Helcim.js takes the form from here; the loading state is released when the
    // page navigates or the result lands in #helcimResults.
    setTimeout(() => setLoading(false), 4000);
  };

  // Shown when the card is billed somewhere other than the address on file.
  const cardholderFields = (
    <>
      <div className="co-field">
        <label htmlFor="cardHolderName">Name on card</label>
        <Input id="cardHolderName" value={cardHolderName} placeholder="Ada Lovelace"
               onChange={(e) => setCardHolderName(e.target.value)} />
      </div>
      <div className="co-row" style={{ marginTop: 12 }}>
        <div className="co-field">
          <label htmlFor="cardHolderAddress">Billing street address</label>
          <Input id="cardHolderAddress" value={cardHolderAddress} placeholder="120 Queen Street West"
                 onChange={(e) => setCardHolderAddress(e.target.value)} />
        </div>
        <div className="co-field">
          <label htmlFor="cardHolderPostalCode">Postal code</label>
          <Input id="cardHolderPostalCode" value={cardHolderPostalCode} placeholder="M5H 2M9"
                 onChange={(e) => setCardHolderPostalCode(e.target.value)} />
        </div>
      </div>
    </>
  );

  return (
    <div className="co">
      <form onSubmit={handleProcessClick} name="helcimForm" id="helcimForm">
        <input type="hidden" id="token" value={helcimConfig["helcim js token"] || ""} readOnly />
        <input type="hidden" id="language" value="en" readOnly />
        <input type="hidden" id="customerCode" value={helcimConfig.customerCode || ""} readOnly />
        {/* A $0.00 amount tokenizes and verifies the card without charging it
            here. The plan's first charge is taken the moment the card is stored
            (usage.services.subscription_billing), and the page confirms it. */}
        <input type="hidden" id="amount" value="0.00" readOnly />
        <input type="hidden" id="g-recaptcha-response" value={grecaptcha} readOnly />

        <div className="co-field">
          <label htmlFor="cardNumber">Card number</label>
          <Input id="cardNumber" value={cardNumber} inputMode="numeric"
                 placeholder="1234 1234 1234 1234" autoComplete="cc-number"
                 onChange={(e) => setCardNumber(e.target.value)} />
        </div>

        <div className="co-row" style={{ marginTop: 12 }}>
          <div className="co-field">
            <label htmlFor="cardExpiryMonth">Expiry</label>
            <div className="co-row">
              <Input id="cardExpiryMonth" value={cardExpiryMonth} maxLength={2} inputMode="numeric"
                     placeholder="MM" autoComplete="cc-exp-month"
                     onChange={(e) => setCardExpiryMonth(e.target.value)} />
              <Input id="cardExpiryYear" value={cardExpiryYear} maxLength={2} inputMode="numeric"
                     placeholder="YY" autoComplete="cc-exp-year"
                     aria-label="Expiry year"
                     onChange={(e) => setCardExpiryYear(e.target.value)} />
            </div>
          </div>
          <div className="co-field">
            <label htmlFor="cardCVV">Security code</label>
            <Input id="cardCVV" value={cardCVV} maxLength={4} inputMode="numeric"
                   placeholder="CVC" autoComplete="cc-csc"
                   onChange={(e) => setCardCVV(e.target.value)} />
          </div>
        </div>

        <div style={{ marginTop: 16 }}>
          {haveBilling ? (
            <>
              <Checkbox checked={sameAsBilling} onChange={(e) => setSameAsBilling(e.target.checked)}>
                Card is billed to the address above
              </Checkbox>
              {sameAsBilling ? (
                <>
                  <input type="hidden" id="cardHolderName" value={billingName} readOnly />
                  <input type="hidden" id="cardHolderAddress" value={billing.address1 || ""} readOnly />
                  <input type="hidden" id="cardHolderPostalCode" value={billing.postalCode || ""} readOnly />
                </>
              ) : (
                <div style={{ marginTop: 14 }}>{cardholderFields}</div>
              )}
            </>
          ) : (
            cardholderFields
          )}
        </div>

        {chargeNote}  {/* brings its own .co-charge shell — see ChargeNote */}

        <Button block type="primary" htmlType="submit" loading={loading}
                className="co-submit" onClick={handleProcessClick}>
          {submitText}
        </Button>

        <div className="co-note">
          Your card details go straight to Helcim, our payment provider. Smilepass never sees or
          stores them.
        </div>

        <div ref={resultRef} id="helcimResults" />
      </form>
    </div>
  );
};

export default PaymentForm;
